import React from 'react';
import { motion } from 'framer-motion';

interface RevealProps {
  children: React.ReactNode;
  width?: "fit-content" | "100%";
  delay?: number;
  className?: string;
  duration?: number;
}

/**
 * Reveal: General purpose wrapper for fading elements in as they scroll into view.
 * OPTIMIZED: Uses viewport.once = true to unmount observers after triggering.
 */
export const Reveal: React.FC<RevealProps> = ({ 
  children, 
  width = "fit-content", 
  delay = 0, 
  className = "",
  duration = 0.6 // Faster duration
}) => {
  return (
    <div style={{ width }} className={`${className} will-change-transform`}>
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 20 }, // Reduced distance
          visible: { opacity: 1, y: 0 },
        }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "0px 0px -50px 0px" }} // Trigger slightly earlier
        transition={{ duration, delay, ease: "easeOut" }}
      >
        {children}
      </motion.div>
    </div>
  );
};

interface RevealGroupProps {
  children: React.ReactNode;
  className?: string;
  interval?: number;
}

export const RevealGroup: React.FC<RevealGroupProps> = ({ 
  children, 
  className, 
  interval = 0.1 
}) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "0px 0px -50px 0px" }}
      transition={{ staggerChildren: interval }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const RevealItem: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className 
}) => {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { 
          opacity: 1, 
          y: 0, 
          transition: { duration: 0.5, ease: "easeOut" } 
        }
      }}
      className={`${className} will-change-transform`}
    >
      {children}
    </motion.div>
  );
};